import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { eventsApi, Event, EmailAudience, registrationsApi } from '../api/client';
import { getErrorMessage } from '../utils/errorHandler';

// Import new components
import EventHeader from './event/EventHeader';
import EventEditForm, { EventFormData } from './event/EventEditForm';
import EventDetails from './event/EventDetails';
import EnhancedEmailModal, { EnhancedEmailData } from './email/EnhancedEmailModal';
import SponsorManager from './event/SponsorManager';

interface EventManagerProps {
  event: Event;
  onUpdate: (event: Event) => void;
  onDelete: (eventId: number) => void;
  onArchive?: (eventId: number) => void;
  onRestore?: (eventId: number) => void;
  onInviteClick?: () => void;
  onExportClick?: () => void;
}

const EventManager: React.FC<EventManagerProps> = ({ 
  event, 
  onUpdate, 
  onDelete, 
  onArchive, 
  onRestore,
  onInviteClick,
  onExportClick 
}) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSponsorManager, setShowSponsorManager] = useState(false);
  const [emailAudience, setEmailAudience] = useState<EmailAudience>('attendees');
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [formData, setFormData] = useState<EventFormData>({
    title: event.title,
    description: event.description || '',
    venue: event.venue,
    date_time: format(new Date(event.date_time), "yyyy-MM-dd'T'HH:mm"),
    end_time: event.end_time ? format(new Date(event.end_time), "yyyy-MM-dd'T'HH:mm") : '',
    max_attendees: event.max_attendees,
    reserved_tickets: event.reserved_tickets || 0,
    admission_policy: event.admission_policy || 'review',
    waitlist_when_full: event.waitlist_when_full ?? true,
    show_attendee_list: event.show_attendee_list || false,
    event_type: event.event_type || 'conference' as 'social' | 'meetup' | 'conference',
    google_maps_link: event.google_maps_link || '',
    entry_requirement: event.entry_requirement || '',
    badge_subtitle: event.badge_subtitle || '',
    badge_title: event.badge_title || '',
  });
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    // Update formData when event prop changes
    setFormData({
      title: event.title,
      description: event.description || '',
      venue: event.venue,
      date_time: format(new Date(event.date_time), "yyyy-MM-dd'T'HH:mm"),
      end_time: event.end_time ? format(new Date(event.end_time), "yyyy-MM-dd'T'HH:mm") : '',
      max_attendees: event.max_attendees,
      reserved_tickets: event.reserved_tickets || 0,
      admission_policy: event.admission_policy || 'review',
      waitlist_when_full: event.waitlist_when_full ?? true,
      show_attendee_list: event.show_attendee_list || false,
      event_type: event.event_type || 'conference' as 'social' | 'meetup' | 'conference',
      google_maps_link: event.google_maps_link || '',
      entry_requirement: event.entry_requirement || '',
      badge_subtitle: event.badge_subtitle || '',
      badge_title: event.badge_title || '',
    });
    // Reset editing state when switching events
    setEditing(false);
  }, [event]);

  useEffect(() => {
    // Load registrations when email modal is opened
    if (showEmailModal) {
      loadRegistrations(emailAudience);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmailModal, emailAudience]);

  const loadRegistrations = async (audience: EmailAudience) => {
    try {
      const data = await registrationsApi.getEventRegistrations(event.id);
      const filtered = audience === 'invitees'
        // Still holding an invitation: invited or opened, and has a code to link to
        ? data.filter((r: any) => r.invitation_code && (r.status === 'invited' || r.status === 'opened' || r.status === 'invitation_opened'))
        : data.filter((r: any) => r.is_approved);
      setRegistrations(filtered);
    } catch (error) {
      console.error('Failed to load registrations:', error);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const updatedEvent = await eventsApi.updateEvent(event.id, {
        title: formData.title,
        description: formData.description,
        venue: formData.venue,
        // Send datetime as local time string with seconds, not ISO (which converts to UTC)
        date_time: formData.date_time + ':00',
        end_time: formData.end_time ? formData.end_time + ':00' : undefined,
        timezone: event.timezone || 'Europe/London',
        max_attendees: formData.max_attendees,
        reserved_tickets: formData.reserved_tickets,
        admission_policy: formData.admission_policy,
        waitlist_when_full: formData.waitlist_when_full,
        show_attendee_list: formData.show_attendee_list,
        event_type: formData.event_type,
        google_maps_link: formData.google_maps_link || undefined,
        entry_requirement: formData.entry_requirement?.trim() || undefined,
        badge_subtitle: formData.badge_subtitle?.trim() || undefined,
        badge_title: formData.badge_title?.trim() || undefined,
      });
      onUpdate(updatedEvent);
      setEditing(false);
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmMessage = event.status === 'draft' 
      ? 'Are you sure you want to delete this draft event? This action cannot be undone.'
      : 'Are you sure you want to permanently delete this event? This will also delete all registrations and data. This action cannot be undone.';
    
    if (window.confirm(confirmMessage)) {
      try {
        await eventsApi.deleteEvent(event.id);
        onDelete(event.id);
      } catch (error) {
        alert(getErrorMessage(error));
      }
    }
  };

  const handleArchive = async () => {
    if (!onArchive) return;
    
    if (window.confirm('Are you sure you want to lock this event? This will make it read-only and prevent any further changes.')) {
      try {
        await eventsApi.archiveEvent(event.id);
        onArchive(event.id);
      } catch (error) {
        alert(getErrorMessage(error));
      }
    }
  };

  const handleRestore = async () => {
    if (!onRestore) return;
    
    if (window.confirm('Are you sure you want to restore this event? This will allow editing again.')) {
      try {
        await eventsApi.restoreEvent(event.id);
        onRestore(event.id);
      } catch (error) {
        alert(getErrorMessage(error));
      }
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = event.status === 'draft' ? 'active' : 'draft';
    const message = newStatus === 'active' 
      ? 'Are you sure you want to publish this event? It will be visible on the public events page.'
      : 'Are you sure you want to unpublish this event? It will be hidden from the public events page.';
    
    if (window.confirm(message)) {
      try {
        await eventsApi.toggleEventStatus(event.id);
        // Reload the event to get the updated status
        const updatedEvent = await eventsApi.getEvent(event.id);
        onUpdate(updatedEvent);
      } catch (error) {
        alert(getErrorMessage(error));
      }
    }
  };

  const handleSendEmail = async (data: EnhancedEmailData) => {
    setSendingEmail(true);
    
    try {
      await eventsApi.sendEmailBroadcast(
        event.id, 
        data.subject,
        data.body,
        data.includeTicketInfo,
        data.recipientIds,
        emailAudience,
        data.includeAttendanceButtons
      );
      const recipientCount = data.recipientIds?.length || registrations.length;
      alert(`Email sent successfully to ${recipientCount} recipients!`);
      setShowEmailModal(false);
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleOpenEmailModal = () => {
    setRegistrations([]);
    setEmailAudience('attendees');
    setShowEmailModal(true);
  };

  const handleOpenEmailInviteesModal = () => {
    setRegistrations([]);
    setEmailAudience('invitees');
    setShowEmailModal(true);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <EventHeader
          event={event}
          editing={editing}
          onToggleEdit={() => setEditing(!editing)}
          onToggleStatus={handleToggleStatus}
          onArchive={handleArchive}
          onRestore={handleRestore}
          onDelete={handleDelete}
          onEmailClick={handleOpenEmailModal}
          onEmailInviteesClick={handleOpenEmailInviteesModal}
          onInviteClick={onInviteClick}
          onExportClick={onExportClick}
          onCreateMediaClick={() => navigate(`/organizers/events/${event.id}/media`)}
          onSponsorsClick={() => setShowSponsorManager(true)}
        />

        {editing ? (
          <EventEditForm
            formData={formData}
            loading={loading}
            onSubmit={handleUpdate}
            onChange={setFormData}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <EventDetails event={event} />
        )}
      </div>
      
      {showEmailModal && (
        <EnhancedEmailModal
          key={emailAudience}
          eventId={event.id}
          eventTitle={event.title}
          audience={emailAudience}
          recipients={registrations.map(r => ({
            id: r.id,
            name: r.name || `${r.given_name} ${r.family_name}`.trim(),
            email: r.email,
            company: r.company,
            status: r.status,
            is_approved: r.is_approved,
            relationship_type: r.relationship_type,
            reconfirmed_at: r.reconfirmed_at,
            selected: true  // Everyone in the audience is selected by default
          }))}
          onSubmit={handleSendEmail}
          onClose={() => {
            setShowEmailModal(false);
          }}
          sending={sendingEmail}
        />
      )}

      {showSponsorManager && (
        <SponsorManager
          eventId={event.id}
          eventTitle={event.title}
          onClose={() => setShowSponsorManager(false)}
        />
      )}
    </div>
  );
};

export default EventManager;